import React, { useState, useContext, useMemo, useEffect } from 'react';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import {
  CameraDark,
  CloseIcon,
  CloudUpload,
  CloudUploadDark,
  FileUpload as FileUploadIcon,
  DeviceSelected as UploadedIcon,
} from 'src/assets/images';
import { compressImage } from 'src/helpers/jpeg-compression';
import DocInfoModal from '../doc-info-modal/doc-info-modal';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import TealiumEvents from 'src/helpers/tealium-events';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import {
  filterObjectByKeys,
  getImageSizeFromBase64,
  matchesKVP,
} from 'src/helpers/functional-helpers';
import { ShowIf } from 'src/components';
import { Prompt } from 'src/features/common/components';
import { ErrorCodeTooltip } from '../error-code-tooltip/error-code-tooltip';
import DocumentImageCaptureModal from 'src/components/document-image-capture-modal/document-image-capture-modal';
import { ApplicationContext } from 'src/features/onboarding/context';
import OnboardingTealiumService from 'src/features/onboarding/services/onboarding-tealium-services';
import MiniAppEvents from 'src/helpers/miniapp-events';
import {
  DocumentWithOptionalBody,
  IDDocsUploaded,
  IdItem,
  OnboardingDocumentTypeEnum,
  UploadError,
} from 'src/features/onboarding/types/upload-documents';
import { Hint } from '../../types/application/pending-app';
import { Environment } from 'src/Environment';
import { cn } from 'src/utils/cn';
import useImageUpload from 'src/features/onboarding/hooks/use-image-upload';
import { ImageUploadActionType } from 'src/features/onboarding/types/image-upload-reducer-actions';
import { ImageUploadJourney } from 'src/features/onboarding/context/image-upload-context';
import { removeBase64Prefix } from 'src/helpers/formatting';
import {
  base64ToFile,
  compressIfJpegCached,
  fileTypes,
  getBase64DataOnly,
  getFileNames,
  getFileType,
} from 'src/features/onboarding/helpers/file-upload-helpers';
import { notify, NotifyPosition, Toast } from '../toast/toast';
import { PAGE_NAME } from '../../types/tealium-service';

// MIME types
export enum FileTypes {
  jpeg = 'image/jpeg',
  png = 'image/png',
  pdf = 'application/pdf',
}

const MimeTypeExtensionMap = {
  [FileTypes.pdf]: ['pdf'],
  [FileTypes.jpeg]: ['jpeg', 'jpg'],
  [FileTypes.png]: ['png'],
};

type Point = {
  title: string;
  message: string;
};

type SubtextProps = {
  description?: Point[];
  subtext?: string;
  acceptedFileText?: string;
  boldedText?: string;
  disabled?: boolean;
  lightMode?: boolean;
  isSubTextBold?: boolean;
};

const Subtext = ({
  description,
  subtext,
  acceptedFileText,
  boldedText,
  disabled,
  lightMode,
  isSubTextBold,
}: SubtextProps) => {
  const subTextClass = cn({
    ['!cursor-not-allowed']: disabled,
    ['text-grey-400']: !lightMode,
    ['font-bold']: isSubTextBold,
  });

  const boldedTextClass = cn({
    ['font-bold']: true,
    ['text-grey-400']: !lightMode,
  });

  return (
    <>
      {!description ? (
        <>
          {subtext && <p className={subTextClass}>{subtext}</p>}
          {acceptedFileText && (
            <p className={subTextClass}>Accepted file type: {acceptedFileText}</p>
          )}
        </>
      ) : (
        <ul>
          {description.map((point) => (
            <li key={`point_${point.title}`} className={`${disabled && '!cursor-not-allowed'}`}>
              <b>{point.title}</b>: {point.message}
            </li>
          ))}
        </ul>
      )}
      <p className={boldedTextClass}>{boldedText}</p>
    </>
  );
};

export type FileUploadProps = {
  title: string;
  subtext: string;
  descriptionPoints?: { title: string; message: string }[];
  boldedText?: string;
  fileLimitText: string;
  documentType: OnboardingDocumentTypeEnum;
  idNumbers?: IdItem[];
  passportNumbers?: IdItem[];
  sizeLimit: number;
  allowedFileTypes: FileTypes[];
  unsupportedFileTypeErrorMessage?: string;
  setDeletedDocs: (file: DocumentWithOptionalBody) => void;
  automationIdPrefix: string;
  inMiniProgram?: boolean;
  isPending: boolean;
  hints?: Hint[];
  maxFiles: number;
  updateIdDocsCount?: (contentDocumentId: string) => void;
  lightMode?: boolean;
  onDocumentUpload?: () => void;
  onShowIdModal?: () => void;
  onUploadError?: (message: string) => void;
  customerType?: string;
  titleOriginal?: string;
  pageName: PAGE_NAME;
  error?: UploadError;
};

export const FileUpload = (props: FileUploadProps) => {
  const [showIdModal, setShowIdModal] = useState<boolean>(false);
  const [isWebcamUpload, setIsWebcamUpload] = useState<boolean>(false);
  const [isFileUpload, setIsFileUpload] = useState<boolean>(false);
  const maxSize = props.sizeLimit;
  const allowedFileTypes = props.allowedFileTypes;
  const handleTechnicalError = useTechnicalErrorHandler();
  const [showWebcamModal, setShowWebcamModal] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const lightMode: boolean = props.lightMode || false;

  const { application } = useContext(ApplicationContext);
  const { imageUploadState, imageUploadDispatch } = useImageUpload();

  const imageJoinEnabled = useMemo(
    (): boolean => !Environment.current.disabledFeatures.imageJoin,
    [],
  );

  const isIdOrPassport = useMemo(
    (): boolean =>
      props.documentType === OnboardingDocumentTypeEnum.ID_TYPE ||
      props.documentType === OnboardingDocumentTypeEnum.PASSPORT,
    [],
  );

  const isForeignNationalDocument = useMemo(
    (): boolean =>
      props.documentType === OnboardingDocumentTypeEnum.PASSPORT ||
      props.documentType === OnboardingDocumentTypeEnum.REFUGEE_LETTER ||
      props.documentType === OnboardingDocumentTypeEnum.ASYLUM_SEEKER ||
      props.documentType === OnboardingDocumentTypeEnum.WORK_PERMIT ||
      props.documentType === OnboardingDocumentTypeEnum.PERMANENT_RESIDENCY,
    [],
  );

  const idOptimisationJourneyEnabled = useMemo(
    (): boolean => !Environment.current.disabledFeatures.idOptimisation,
    [],
  );

  const taggingJourney = useMemo(() => {
    switch (true) {
      case !!application.pendingApp?.isPending:
        return 'pending app';

      case !!application.brokerLogin || application.agentId:
        return 'third party';

      case application.deviceActivationTagging:
        return 'register device';

      default:
        return 'onboarding';
    }
  }, []);

  const idTypeList = [
    OnboardingDocumentTypeEnum.ID_BOOK,
    OnboardingDocumentTypeEnum.ID_CARD,
    OnboardingDocumentTypeEnum.PASSPORT,
  ];

  const disableDrop = (idDocsUploaded: IDDocsUploaded[]) => {
    const maxSmartCards = imageJoinEnabled ? 2 : 1;
    return idDocsUploaded.every(
      (item) =>
        item.numOfSmartCards === maxSmartCards ||
        item.numOfGreenBooks === 1 ||
        item.numOfPassports === 1,
    );
  };

  const allowIdDocModal = () => {
    if (isFileUpload) {
      return !idOptimisationJourneyEnabled || !isIdOrPassport;
    }
    return true;
  };

  const multipleUpload = (): boolean => {
    return idOptimisationJourneyEnabled && isIdOrPassport;
  };

  const IdNumberOrDocNameIsTBD = (docs: DocumentWithOptionalBody[]) =>
    docs.length > 0 &&
    docs.some((document) => document.idNumber === 'TBD' || document.documentName === 'TBD');

  const checkForPermitOrResidency = (docs: DocumentWithOptionalBody[]) =>
    docs.length > 0 &&
    docs.some(
      (document) =>
        (document.documentType === OnboardingDocumentTypeEnum.WORK_PERMIT ||
          document.documentType === OnboardingDocumentTypeEnum.PERMANENT_RESIDENCY) &&
        document.idNumber === 'TBD',
    );

  const removeUploadedFile = (fileToRemove: DocumentWithOptionalBody) => {
    const newDocs = imageUploadState.documents.filter((file) => !Object.is(file, fileToRemove));

    props.setDeletedDocs(fileToRemove);
    imageUploadDispatch({
      type: ImageUploadActionType.SET_STEP_INDEX,
      stepIndex: imageUploadState.stepIndex - 1,
    });
    imageUploadDispatch({
      type: ImageUploadActionType.REMOVE_DOCUMENTS,
      docs: newDocs,
      fileToRemove,
    });
  };

  const compressOptions = {
    idDocument: { maxSizeMB: 1, maxWidthOrHeight: 640 },
    default: { maxSizeMB: 3 },
  };

  type CurrentFile = {
    fileData: string;
    fileName: string;
    fileUrl: string;
    fileExt: string;
  };

  const readCurrentFile = (acceptedFile: File) => {
    return new Promise<CurrentFile>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result && typeof reader.result === 'string') {
          resolve({
            fileData: getBase64DataOnly(reader.result),
            fileName: acceptedFile.name,
            fileUrl: URL.createObjectURL(acceptedFile),
            fileExt: acceptedFile.type.substring(acceptedFile.type.indexOf('/') + 1),
          });
        }
      };
      reader.readAsDataURL(acceptedFile);
    });
  };

  const readUploadedFile = async (file: File) => {
    const compressedFile = await compressImage(file, compressOptions.idDocument);
    readCurrentFile(compressedFile)
      .then((file) => {
        const newDoc: DocumentWithOptionalBody = {
          documentBody: file.fileData,
          documentUrl: file.fileUrl,
          documentType: props.documentType,
          documentName: file.fileName,
          idNumber: isIdOrPassport || isForeignNationalDocument ? 'TBD' : undefined,
          unconfirmed: isIdOrPassport || isForeignNationalDocument,
        };

        imageUploadDispatch({
          type: ImageUploadActionType.UPDATE_CURRENT_DOCUMENTS,
          docs: [...imageUploadState.currentDocuments, newDoc],
        });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const readCapturedFile = async (file: File) => {
    const compressedFile = await compressImage(file, compressOptions.idDocument);
    readCurrentFile(compressedFile)
      .then((file) => {
        const newDoc: DocumentWithOptionalBody = {
          documentBody: file.fileData,
          documentUrl: file.fileUrl,
          documentType: props.documentType,
          documentName: `${props.documentType}.${file.fileExt}`,
          idNumber: isIdOrPassport || isForeignNationalDocument ? 'TBD' : undefined,
          unconfirmed: isIdOrPassport || isForeignNationalDocument,
        };

        imageUploadDispatch({
          type: ImageUploadActionType.UPDATE_CURRENT_DOCUMENTS,
          docs: [...imageUploadState.currentDocuments, newDoc],
        });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const readMultipleUploadedFiles = (files: File[]) => {
    Promise.all(
      compressIfJpegCached(
        files,
        isIdOrPassport ? compressOptions.idDocument : compressOptions.default,
      ),
    ).then((newFiles) => {
      Promise.all(newFiles.map(readCurrentFile))
        .then((uploadedFiles) => {
          const newDocuments: DocumentWithOptionalBody[] = [];
          if (imageUploadState.documents.length > 0) {
            imageUploadState.documents.map((element) => newDocuments.push(element));
          }
          uploadedFiles.forEach((element) => {
            newDocuments.push({
              documentBody: element.fileData,
              documentUrl: element.fileUrl,
              documentType: props.documentType,
              documentName: element.fileName,
              idNumber: isIdOrPassport || isForeignNationalDocument ? 'TBD' : undefined,
              unconfirmed: isIdOrPassport || isForeignNationalDocument,
            });
          });

          if (IdNumberOrDocNameIsTBD(newDocuments)) {
            if (props.onShowIdModal) {
              props.onShowIdModal();
            }
            setShowIdModal(allowIdDocModal());
            imageUploadDispatch({
              type: ImageUploadActionType.UPDATE_CURRENT_DOCUMENTS,
              docs: newDocuments,
            });
            return;
          }

          if (checkForPermitOrResidency(newDocuments)) {
            imageUploadDispatch({
              type: ImageUploadActionType.UPDATE_CURRENT_DOCUMENTS,
              docs: newDocuments,
            });
            return;
          }

          imageUploadDispatch({
            type: ImageUploadActionType.UPDATE_DOCUMENTS,
            docs: newDocuments,
          });
        })
        .catch((error) => {
          console.error(error);
        });
    });
  };

  const handleDocumentUpload = (
    acceptedFiles: File[],
    event?: DropEvent,
    imitateEvent?: boolean,
  ) => {
    if (props.onDocumentUpload) {
      props.onDocumentUpload();
    }

    // Upload proof of banking etc... OR normal journey
    if (!isIdOrPassport || !idOptimisationJourneyEnabled) {
      readMultipleUploadedFiles(acceptedFiles);
      return;
    }

    // File upload functionality (Revamp)
    if ((event || imitateEvent) && idOptimisationJourneyEnabled) {
      if (!event) {
        readUploadedFile(acceptedFiles[0]);
        return;
      }
      switch (event.type) {
        case 'drop':
          imageUploadDispatch({
            type: ImageUploadActionType.SELECT_ID_TYPE,
            journey: ImageUploadJourney.UPLOAD,
            showSelectIdType: true,
            documentType: props.documentType,
          });
          readUploadedFile(acceptedFiles[0]);
          return;
        case 'change':
          readUploadedFile(acceptedFiles[0]);
          return;
        default:
          throw new Error(
            `Upload your documents: file upload: Unexpected event on drop: ${JSON.stringify(
              event,
            )}`,
          );
      }
    }

    // Open camera functionality (Revamp)
    if (idOptimisationJourneyEnabled && isIdOrPassport) {
      readCapturedFile(acceptedFiles[0]);
      return;
    }
    readMultipleUploadedFiles(acceptedFiles);
  };

  const onDrop = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
    event?: DropEvent,
    imitateEvent?: boolean,
  ) => {
    if (fileRejections.length > 0) {
      return;
    }

    setIsFileUpload(true);

    if (props.onDocumentUpload) {
      props.onDocumentUpload();
    }

    handleDocumentUpload(acceptedFiles, event, imitateEvent);
  };

  const extractDoc = (
    docs: DocumentWithOptionalBody[],
    oldFileName: string,
    documentType: OnboardingDocumentTypeEnum,
  ) => {
    return docs.filter((file) => {
      if (documentType === OnboardingDocumentTypeEnum.ID_TYPE) {
        return matchesKVP(file, {
          documentName: oldFileName,
          idNumber: 'TBD',
          documentType: OnboardingDocumentTypeEnum.ID_TYPE,
        });
      } else {
        return matchesKVP(file, {
          documentName: oldFileName,
          documentType: documentType,
        });
      }
    })[0];
  };

  const getUpdatedDoc = (
    oldFileName: string,
    newId: string,
    newIdType: OnboardingDocumentTypeEnum,
    documentType: OnboardingDocumentTypeEnum,
    newFileName: string,
  ) => {
    const updatedDoc = extractDoc(imageUploadState.currentDocuments, oldFileName, documentType);

    const newDoc = {
      ...updatedDoc,
      unconfirmed: false,
      idNumber: newId,
      documentName: newFileName ? newFileName : oldFileName,
      documentType: newIdType ? newIdType : documentType,
    };

    if (!idOptimisationJourneyEnabled || isWebcamUpload) {
      imageUploadDispatch({
        type: ImageUploadActionType.ADD_ID_DOCS,
        doc: newDoc,
      });
    }

    return newDoc;
  };

  const updateIdFiles = (
    oldFileNames: string[],
    id: string,
    idType: OnboardingDocumentTypeEnum,
    documentType: OnboardingDocumentTypeEnum,
    newFileName?: string,
  ) => {
    const newDocs = oldFileNames.reduce((accum, oldFileName) => {
      const newDocList: DocumentWithOptionalBody[] = [];
      newDocList.push(getUpdatedDoc(oldFileName, id, idType, documentType, newFileName ?? ''));

      const oldList = accum.filter((file) => {
        if (file.documentType === OnboardingDocumentTypeEnum.ID_TYPE) {
          return !matchesKVP(file, {
            documentName: oldFileName,
            documentType: OnboardingDocumentTypeEnum.ID_TYPE,
            idNumber: 'TBD',
          });
        } else {
          return !matchesKVP(file, {
            documentName: oldFileName,
            documentType: documentType,
          });
        }
      });
      accum = oldList.concat(newDocList);
      return accum;
    }, imageUploadState.documents);

    imageUploadDispatch({
      type: ImageUploadActionType.UPDATE_DOCUMENTS,
      docs: newDocs,
      currentDocs: [],
    });
  };

  const hasUploadedFile = () =>
    imageUploadState.documents.findIndex(
      (file) =>
        file.documentType === props.documentType ||
        (isIdOrPassport && idTypeList.includes(file.documentType ?? '')),
    ) >= 0;

  const removeMultipleFiles = () => {
    const newDocs = imageUploadState.documents.filter(
      (doc) => !doc.unconfirmed && doc.documentName !== 'TBD',
    );
    imageUploadDispatch({
      type: ImageUploadActionType.REMOVE_DOCUMENTS,
      docs: newDocs,
    });
  };

  const onDropRejected = (fileRejections: FileRejection[]) => {
    if (fileRejections[0].errors[0].code === 'file-invalid-type') {
      const errorMessage =
        props.unsupportedFileTypeErrorMessage ||
        `File upload failed: Incorrect file type, only ${fileTypes(allowedFileTypes)} supported.`;
      if (imageUploadState.showModal) {
        notify({
          children: errorMessage,
          autoClose: 5000,
          position: NotifyPosition.top_center,
          type: 'warning',
        });
        return;
      }
      toastMessageServiceSingleton.addError(errorMessage, null, 'N/A', true, null, true);
      if (props.onUploadError) {
        props.onUploadError(errorMessage);
      }
    } else if (fileRejections[0].errors[0].code === 'file-too-large') {
      const errorMessage = `File upload failed: Maximum file size of ${
        maxSize > 1000000 ? `${maxSize / 1024000}MB` : `${maxSize / 1024}KB`
      } exceeded.`;
      if (imageUploadState.showModal) {
        notify({
          children: errorMessage,
          autoClose: 5000,
          position: NotifyPosition.top_center,
          type: 'warning',
        });
        return;
      }
      toastMessageServiceSingleton.addError(errorMessage, null, 'N/A', true, null, true);
      if (props.onUploadError) {
        props.onUploadError(errorMessage);
      }
    } else if (fileRejections[0].errors[0].code === 'dxl-upload-error') {
      TealiumEvents.dxlErrorUploadMiniApp(fileRejections[0].errors[0].message, 'document upload');
      // @ts-ignore
      handleTechnicalError(fileRejections[0].errors[0].message, true, true);
    } else {
      const errorMessage = fileRejections[0].errors[0].message;
      if (imageUploadState.showModal) {
        notify({
          children: errorMessage,
          autoClose: 5000,
          position: NotifyPosition.top_center,
          type: 'warning',
        });
        return;
      }
      toastMessageServiceSingleton.addError(errorMessage, null, 'N/A', false, null, true);
      if (props.onUploadError) {
        props.onUploadError(errorMessage);
      }
    }
  };

  const fileExtensionValidator = (file: File) => {
    if (file.name === undefined) return null;
    const ext = file.name.substring(file.name.lastIndexOf('.') + 1);

    const filteredExtensions = props.allowedFileTypes
      .map((mime) => MimeTypeExtensionMap[mime])
      .flat();
    if (filteredExtensions.some((item) => item == ext)) {
      return null;
    }
    return {
      code: 'file-invalid-type',
      message: `Invalid file extension(${ext}).`,
    };
  };

  const { isDragActive, getRootProps, getInputProps, isDragReject, open } = useDropzone({
    onDrop,
    onDropRejected,
    accept: filterObjectByKeys(MimeTypeExtensionMap, allowedFileTypes),
    minSize: 0,
    maxSize,
    noClick: !props.inMiniProgram,
    maxFiles: props.maxFiles,
    disabled: disabled,
    multiple: !multipleUpload(),
    validator: fileExtensionValidator,
  });

  const miniappOnClick = () => {
    MiniAppEvents.listenForMessage((observer) => {
      const message = MiniAppEvents.readMessage();
      if (message.type === props.documentType) {
        observer.disconnect();
        if (message.error) {
          //Occurs if DXL upload fails on mini-app side
          onDropRejected([
            //@ts-ignore
            { errors: [{ code: message.errorType, message: message.errorMessage }] },
          ]);
        } else {
          if (message.url) {
            let documentCount = 0;

            if (imageUploadState.documents.length > 0) {
              documentCount = imageUploadState.documents.filter(
                (item) =>
                  item.documentType === message.type ||
                  (message.type === OnboardingDocumentTypeEnum.ID_TYPE &&
                    idTypeList.includes(item.documentType ?? '')),
              ).length;
            }

            message.name = message.type + (documentCount > 0 ? ` (${documentCount})` : '');
            MiniAppEvents.downloadBase64FileURL(message.url)
              .then((res) => {
                const base64Data = res;

                const fileType = getFileType(base64Data);

                if (fileType.mime && allowedFileTypes.includes(fileType?.mime)) {
                  //If message type is one of the allowed types.
                  const messageNameAndType = message.name.split('.')[0] + fileType.ext;
                  const file = base64ToFile(base64Data, messageNameAndType, fileType.mime);
                  handleDocumentUpload([file]);
                } else {
                  const messageNameAndType = message.name.split('.')[0] + fileType.ext;
                  const file = base64ToFile(base64Data, messageNameAndType, fileType.mime);
                  //Reject file with invalid type
                  onDropRejected([
                    { errors: [{ code: 'file-invalid-type', message: 'Invalid file type' }], file },
                  ]);
                }
              })
              .catch((error) => {
                //Occurs if DXL download fails on portal side
                TealiumEvents.dxlErrorDownloadMiniApp(error, 'document upload');
                // @ts-ignore
                handleTechnicalError(error, true, true);
              });
          }
        }
      }
    });
    MiniAppEvents.sendMessage({
      messageType: props.documentType,
      maxSize: maxSize,
    });
  };

  const openCameraClicked = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (disabled || (props.isPending && !props.hints)) {
      return;
    }
    setIsFileUpload(false);
    TealiumEvents.docsOpenCameraButton(props.customerType, props.documentType);
    if (idOptimisationJourneyEnabled && isIdOrPassport) {
      imageUploadDispatch({
        type: ImageUploadActionType.TAKE_PHOTO_TIP,
        showModal: true,
        documentType: props.documentType,
      });
      return;
    }
    setShowWebcamModal(!disabled);
  };

  const onClickUpload = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    application.tapOnPhoneJourney
      ? OnboardingTealiumService.onDocumentUploadToPAction(props.documentType)
      : OnboardingTealiumService.onDocumentUploadAction(props.documentType, taggingJourney);
    if (disabled || (props.isPending && !props.hints)) {
      return;
    }
    setIsFileUpload(true);
    if (isIdOrPassport && idOptimisationJourneyEnabled) {
      imageUploadDispatch({
        type: ImageUploadActionType.SELECT_ID_TYPE,
        journey: ImageUploadJourney.UPLOAD,
        showSelectIdType: true,
        documentType: props.documentType,
      });
      return;
    }
    open();
  };

  const filterCurrentDocs = (
    currentDocs: DocumentWithOptionalBody[],
    docType: OnboardingDocumentTypeEnum,
  ) => {
    return currentDocs.filter(
      (doc) =>
        doc.documentType === docType ||
        (isIdOrPassport && idTypeList.includes(doc.documentType ?? '')),
    );
  };

  const onImageCapture = (imageSrc: string) => {
    const base64Data = imageSrc ? removeBase64Prefix(imageSrc) : '';
    const fileType = getFileType(base64Data);

    if (getImageSizeFromBase64(base64Data) > maxSize) {
      const errorMessage = `File upload failed: Maximum file size of ${
        maxSize > 1000000 ? `${maxSize / 1024000}MB` : `${maxSize / 1024}KB`
      } exceeded.`;
      toastMessageServiceSingleton.addError(errorMessage, null, 'N/A', false, null, true);
      return;
    }

    if (fileType && fileType !== null && allowedFileTypes.includes(fileType.mime)) {
      //If message type is one of the allowed types.
      const file = base64ToFile(base64Data, 'TBD', fileType.mime);
      setIsWebcamUpload(true);
      handleDocumentUpload([file]);

      TealiumEvents.docsCaptureImage(props.customerType);
    } else {
      //Reject file with invalid type
      const file = base64ToFile(base64Data, 'TBD', fileType.mime);
      onDropRejected([
        {
          errors: [{ code: 'file-invalid-type', message: 'Incorrect file type' }],
          file: file,
        },
      ]);
    }
  };

  useEffect(() => {
    if (isIdOrPassport && idOptimisationJourneyEnabled) {
      imageUploadDispatch({
        type: ImageUploadActionType.SET_ALLOWED_FILE_TYPES,
        allowedFileTypes: props.allowedFileTypes,
        maxFileSize: props.sizeLimit,
      });
    }
  }, []);

  useEffect(() => {
    if (imageUploadState.selectedFile) {
      readUploadedFile(imageUploadState.selectedFile);
    }
  }, [imageUploadState.selectedFile]);

  useEffect(() => {
    if (
      imageUploadState?.idDocsUploaded &&
      imageUploadState.idDocsUploaded.length &&
      isIdOrPassport
    ) {
      setDisabled(disableDrop(imageUploadState.idDocsUploaded));
    } else {
      const currentFileTypeDocs = imageUploadState.documents.filter(
        (doc) => doc.documentType === props.documentType,
      );
      setDisabled(currentFileTypeDocs.length >= props.maxFiles);
    }
  }, [imageUploadState?.idDocsUploaded, imageUploadState.documents, props.maxFiles]);

  const docTypesMatch = (): boolean => {
    return props.documentType === imageUploadState.documentType;
  };

  useEffect(() => {
    if (imageUploadState.uploadPhoto && isIdOrPassport && docTypesMatch()) {
      open();

      imageUploadDispatch({
        type: ImageUploadActionType.INIT_UPLOAD_PHOTO,
        uploadPhoto: false,
      });
    }
  }, [imageUploadState.uploadPhoto]);

  useEffect(() => {
    if (imageUploadState.takePhoto && isIdOrPassport && docTypesMatch()) {
      onImageCapture(imageUploadState.capturedImage ?? '');
      imageUploadDispatch({
        type: ImageUploadActionType.UPDATE_TAKE_PHOTO,
        takePhoto: false,
      });
    }
  }, [imageUploadState.takePhoto]);

  useEffect(() => {
    if (imageUploadState.documents.length === 0) {
      imageUploadDispatch({
        type: ImageUploadActionType.SET_STEP_INDEX,
        stepIndex: 0,
      });
    }
  }, [imageUploadState.documents]);

  const fileUploadContainerClass = cn({
    ['relative']: true,
    ['!cursor-not-allowed']: disabled || (props.isPending && !props.hints),
  });

  const fileUploadClass = cn({
    ['flex flex-col gap-4 p-6 border-[1px] border-solid rounded-15 border-grey-750 bg-white min-h-26 md:w-232 mx-4']:
      true,
    ['bg-grey-800']: !lightMode,
    ['border-teal border-2']:
      !isDragReject && !(props?.error && props.error.error) && hasUploadedFile(),
    ['bg-white-50 border-teal border-2']: !isDragReject && isDragActive,
    ['border-orange border-2']: isDragReject,
    ['opacity-40 cursor-not-allowed']: props.isPending && !props.hints,
    ['!cursor-not-allowed']: disabled,
    ['border-red-600 border-2']: props?.error && props.error.error,
  });

  const uploadTitleClass = cn({
    ['text-sm md:text-3xl font-medium md:!font-normal text-grey-750']: true,
    ['text-grey-200']: !lightMode,
  });

  const uploadedDocsContainerBorder = cn({
    ['flex flex-col md:flex-row justify-center items-center md:items-start border-2 border-dashed border-grey-750 rounded-15 cursor-pointer']:
      true,
    ['!cursor-not-allowed']: disabled || (props.isPending && !props.hints),
    ['border-grey-400']: !lightMode,
  });

  const uploadedDocsContainer = cn({
    ['flex flex-col md:flex-row items-center md:overflow-x-auto w-3/5 md:w-full h-fit gap-x-5 gap-y-1 mt- pb-4 pt-2 scrollbar']:
      true,
    ['hidden']: isDragActive,
  });

  const uploadButtonsContainer = cn({
    ['flex gap-x-20 flex-wrap justify-center px-4 py-5 text-grey-750']: true,
    ['hidden']: isDragActive,
    ['text-grey-400']: !lightMode,
  });

  const uploadDragImage = cn({
    ['hidden']: true,
    ['flex']: isDragActive && !isDragReject,
  });

  const uploadDragNotAcceptedText = cn({
    ['hidden']: true,
    ['flex text-grey-400 text-xl']: isDragActive && isDragReject,
  });

  const openCameraButton = cn({
    ['items-center hidden gap-2 md:flex']: true,
    ['!cursor-not-allowed']: disabled || (props.isPending && !props.hints),
  });

  const fileLimitTextClass = cn({
    ['text-2xs md:text-base']: true,
    ['text-grey-400']: !lightMode,
  });

  const removeButton = cn('absolute right-0 w-5 h-5 mr-1', {
    ['!cursor-not-allowed']: props.isPending && !props.hints,
  });

  if (props.inMiniProgram) {
    return (
      <div>
        <DocInfoModal
          show={showIdModal && allowIdDocModal()}
          fileNames={getFileNames(imageUploadState.currentDocuments)}
          ids={props.idNumbers || props.passportNumbers}
          isForeignNationalDocument={isForeignNationalDocument}
          documentTypeName={props.titleOriginal}
          documentType={props.documentType}
          onHide={() => setShowIdModal(false)}
          onClose={removeMultipleFiles}
          onSubmission={(fileNames: string[], id: string, idType: OnboardingDocumentTypeEnum) => {
            updateIdFiles(fileNames, id, idType, props.documentType);
          }}
          idDocsUploaded={imageUploadState.idDocsUploaded}
          disabled={false}
          automationIdPrefix={props.automationIdPrefix}
        />

        <div
          {...getRootProps({ className: 'dropzone' })}
          onClick={(event) => {
            event.stopPropagation();
            if (disabled || (props.isPending && !props.hints)) {
              return;
            }
            miniappOnClick();
          }}
          className={fileUploadContainerClass}
        >
          {hasUploadedFile() && !(props?.error && props.error.error) && (
            <img className="absolute right-4 md:right-5" src={UploadedIcon} />
          )}
          <input
            {...getInputProps()}
            automationid={`${props.automationIdPrefix}_Input_Upload_Document`}
          />
          <section className={fileUploadClass}>
            <h3 className={uploadTitleClass}>{props.title}</h3>

            <div className={uploadedDocsContainerBorder}>
              {filterCurrentDocs(imageUploadState.documents, props.documentType).length > 0 && (
                <div className={uploadedDocsContainer}>
                  {filterCurrentDocs(imageUploadState.documents, props.documentType).map(
                    (file, index) => (
                      <div
                        key={`${file.documentName}_${file.documentType}_${index}_${
                          file.idNumber ? file.idNumber : ''
                        }`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex flex-col items-center justify-center h-fit w-full md:w-[unset] py-2 px-3 ml-2 bg-grey-100 rounded-8 relative "
                      >
                        <p className="mr-4 text-center text-ellipsis whitespace-nowrap overflow-hidden md:overflow-visible w-full md:w-[unset] px-4">
                          {file.documentName}
                        </p>
                        <p className="text-3xs">{file.idNumber}</p>
                        <button
                          className={removeButton}
                          onClick={(e) => {
                            if (props.isPending && !props.hints) {
                              return;
                            }
                            removeUploadedFile(file);
                            e.stopPropagation();
                          }}
                          automationid={`${props.automationIdPrefix}_Delete_Uploaded_Document`}
                        >
                          <img src={CloseIcon} alt="X" />
                        </button>
                      </div>
                    ),
                  )}
                </div>
              )}

              <div className={uploadButtonsContainer}>
                <section className="flex items-center gap-2">
                  <img className="w-5 h-5" src={CloudUpload} alt="upload" />
                  <h4 className="text-xs md:text-3xl !font-normal text-center text-grey-750">
                    Upload
                  </h4>
                </section>

                <section className="items-center hidden gap-2 md:flex">
                  <img className="w-8 h-8" src={FileUploadIcon} alt="folder" />
                  <h4 className="text-3xl font-normal ">Drag and Drop</h4>
                </section>

                <button className={openCameraButton} onClick={openCameraClicked}>
                  <img className="w-8 h-8" src={CameraDark} alt="camera" />
                  <span className="text-3xl font-normal ">Open camera</span>
                </button>
              </div>

              <img className={uploadDragImage} src={CloudUpload} alt="cloud" />
              <p className={uploadDragNotAcceptedText}>
                File type not accepted. Please click to retry again
              </p>
            </div>

            <div className="flex text-grey-750">
              <div className="hidden md:flex flex-col max-w-[70%]">
                <Subtext
                  description={props.descriptionPoints}
                  subtext={props.subtext}
                  boldedText={props.boldedText}
                  disabled={disabled}
                  lightMode={lightMode}
                />
              </div>
              <div className="relative mt-auto ml-auto md:mt-0 md:static top-4">
                <ShowIf condition={!!props.fileLimitText}>
                  <p className={fileLimitTextClass}>{props.fileLimitText}</p>
                </ShowIf>
              </div>
            </div>
          </section>
          <div className="flex flex-col mx-8 mt-4 md:hidden text-2xs">
            <Subtext
              description={props.descriptionPoints}
              subtext={props.subtext}
              boldedText={props.boldedText}
              disabled={disabled}
              lightMode={lightMode}
            />
          </div>
        </div>

        {props.isPending && props.hints && props.hints.length > 0 && (
          <ErrorCodeTooltip
            isUpload={true}
            content={props.hints}
            pageName={PAGE_NAME.UPLOAD_DOCUMENTS}
          >
            <Prompt className="mt-4 ml-4" />
          </ErrorCodeTooltip>
        )}
      </div>
    );
  }

  return (
    <div>
      <DocInfoModal
        show={showIdModal}
        fileNames={getFileNames(imageUploadState.currentDocuments)}
        ids={props.idNumbers || props.passportNumbers}
        isForeignNationalDocument={isForeignNationalDocument}
        documentTypeName={props.titleOriginal}
        documentType={props.documentType}
        onHide={() => {
          setShowIdModal(false);
          setIsWebcamUpload(false);
        }}
        onSubmission={(oldFileNames, id, idType, newFileName) => {
          updateIdFiles(oldFileNames, id, idType, props.documentType, newFileName);
        }}
        isWebcamUpload={isWebcamUpload}
        idDocsUploaded={imageUploadState.idDocsUploaded}
        disabled={false}
        automationIdPrefix={props.automationIdPrefix}
      />
      <DocumentImageCaptureModal
        show={showWebcamModal}
        onHide={() => setShowWebcamModal(false)}
        onImageCapture={onImageCapture}
        automationIdPrefix="Onboarding_Create_Account_Your_Details"
        customerType={props.customerType}
      />
      <Toast />

      <div
        {...getRootProps({ className: 'dropzone' })}
        className={fileUploadContainerClass}
        automationid={`${props.automationIdPrefix}_Upload_Drag_Drop`}
      >
        {hasUploadedFile() && !(props?.error && props.error.error) && (
          <img className="absolute right-4" src={UploadedIcon} />
        )}
        <input
          {...getInputProps()}
          automationid={`${props.automationIdPrefix}_Input_Upload_Document`}
        />
        <section className={fileUploadClass}>
          <h3 className={uploadTitleClass}>{props.title}</h3>

          <div className={uploadedDocsContainerBorder} onClick={onClickUpload}>
            {filterCurrentDocs(imageUploadState.documents, props.documentType).length > 0 && (
              <div className={uploadedDocsContainer}>
                {filterCurrentDocs(imageUploadState.documents, props.documentType).map(
                  (file, index) => (
                    <div
                      key={`${file.documentName}_${file.documentType}_${index}_${
                        file.idNumber ? file.idNumber : ''
                      }`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex flex-col items-center justify-center h-fit w-full md:w-[unset] py-2 px-3 ml-2 bg-grey-100 rounded-8 relative "
                    >
                      <p className="mr-4 text-center text-ellipsis whitespace-nowrap overflow-hidden md:overflow-visible w-full md:w-[unset] px-4">
                        {file.documentName}
                      </p>
                      <p className="text-3xs">{file.idNumber}</p>
                      <button
                        className={removeButton}
                        onClick={(e) => {
                          if (props.isPending && !props.hints) {
                            return;
                          }
                          removeUploadedFile(file);
                          e.stopPropagation();
                        }}
                        automationid={`${props.automationIdPrefix}_Delete_Uploaded_Document`}
                      >
                        <img src={CloseIcon} alt="close" />
                      </button>
                    </div>
                  ),
                )}
              </div>
            )}

            <div className={uploadButtonsContainer}>
              <section className="flex items-center gap-2">
                <img className="w-8 h-8" src={CloudUploadDark} alt="upload" />
                <h4 className="text-base md:text-3xl !font-normal  text-center">
                  <span className="underline md:no-underline">Upload</span>{' '}
                  <span className="md:hidden">or</span>{' '}
                  <button className="underline md:hidden" onClick={openCameraClicked}>
                    take a photo
                  </button>
                </h4>
              </section>

              <section
                className="items-center hidden gap-2 cursor-default md:flex"
                onClick={(e) => e.stopPropagation()}
              >
                <img className="w-8 h-8" src={FileUploadIcon} alt="folder" />
                <h4 className="text-3xl font-normal ">Drag and Drop</h4>
              </section>

              <button className={openCameraButton} onClick={openCameraClicked}>
                <img className="w-8 h-8" src={CameraDark} alt="camera" />
                <span className="text-3xl font-normal ">Open camera</span>
              </button>
            </div>

            <img className={uploadDragImage} src={CloudUpload} alt="cloud" />
            <p className={uploadDragNotAcceptedText}>File(s) not accepted. Please retry again</p>
          </div>

          <div className="flex text-grey-750">
            <div className="hidden md:flex flex-col max-w-[70%]">
              <Subtext
                description={props.descriptionPoints}
                subtext={props.subtext}
                boldedText={props.boldedText}
                acceptedFileText={
                  isIdOrPassport ? undefined : fileTypes(props.allowedFileTypes, ',')
                }
                disabled={disabled}
                lightMode={lightMode}
                isSubTextBold={props.documentType === OnboardingDocumentTypeEnum.ID_TYPE}
              />
            </div>
            <div className="relative mt-auto ml-auto md:mt-0 md:static top-4">
              <ShowIf condition={!!props.fileLimitText}>
                <p className={fileLimitTextClass}>{props.fileLimitText}</p>
              </ShowIf>
            </div>
          </div>
        </section>
        {props?.error && props.error.error && (
          <p className="mx-4 mt-1 text-sm text-center text-red-600 md:text-base md:text-left">
            {props.error?.message}
          </p>
        )}

        <div className="flex flex-col mx-8 mt-4 md:hidden text-2xs">
          <Subtext
            description={props.descriptionPoints}
            subtext={props.subtext}
            boldedText={props.boldedText}
            acceptedFileText={isIdOrPassport ? undefined : fileTypes(props.allowedFileTypes, ',')}
            disabled={disabled}
            lightMode={lightMode}
            isSubTextBold={props.documentType === OnboardingDocumentTypeEnum.ID_TYPE}
          />
        </div>
      </div>

      {props.isPending && props.hints && props.hints.length > 0 && (
        <ErrorCodeTooltip
          isUpload={true}
          content={props.hints}
          pageName={PAGE_NAME.UPLOAD_DOCUMENTS}
        >
          <Prompt className="mt-4 ml-4" />
        </ErrorCodeTooltip>
      )}
    </div>
  );
};

export default FileUpload;
